import { ServiceStore } from "@intrasync/stores";
import { RouteType } from "types/RouteType";

const EstimatingDashboardRoute: RouteType = {
  name: "Estimating Dashboard",
  path: "/estimating/dashboard",
  layout: "/app",
  group: "Estimating",
  requiresAuth: true,
  requiresPermission: false,
  hidden: true,
  viewportCard: {
    header: {
      title: "Estimating Dashboard",
      component: () => import("./EstimatingDashboardHeader"),
      enableRefreshButton: true,
      enableBackButton: false,
    },
    body: {
      component: () => import("./EstimatingDashboardBody"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore }) => {
    try {
      // Initialize the dashboard view store
      const EstimatingDashboardViewStore = (
        await import("./EstimatingDashboardViewStore")
      ).default;

      if (!rootStore.estimatingDashboardViewStore) {
        rootStore.estimatingDashboardViewStore =
          new EstimatingDashboardViewStore(rootStore);
      }

      // Load critical estimating stores
      const promises = [];

      // Load bids for quote pipeline metrics
      const bidsStore = rootStore.bidsStore as ServiceStore;
      if (bidsStore) {
        promises.push(bidsStore.find());
      }

      // Load manhole quotes
      const manholeQuotesStore = rootStore.manholeQuotesStore as ServiceStore;
      if (manholeQuotesStore) {
        promises.push(manholeQuotesStore.find());
      }

      // Load opportunities
      const opportunitiesStore = rootStore.opportunitiesStore as ServiceStore;
      if (opportunitiesStore) {
        promises.push(opportunitiesStore.find());
      }

      // Load users for estimator performance
      const userStore = rootStore.userStore as ServiceStore;
      if (userStore) {
        promises.push(userStore.find());
      }

      // Load bid analytics if available
      const bidAnalyticsStore = rootStore.bidAnalyticsStore as ServiceStore;
      if (bidAnalyticsStore) {
        promises.push(
          bidAnalyticsStore.find().catch(() => {
            console.warn("Bid analytics store not accessible");
          })
        );
      }

      // Load opportunity scorer if available
      const opportunityScorerStore =
        rootStore.opportunityScorerStore as ServiceStore;
      if (opportunityScorerStore) {
        promises.push(
          opportunityScorerStore.find().catch(() => {
            console.warn("Opportunity scorer store not accessible");
          })
        );
      }

      // Load projects for context
      const projectsStore = rootStore.projectsStore as ServiceStore;
      if (projectsStore) {
        promises.push(projectsStore.find());
      }

      // Load accounts for customer analysis
      const accountsStore = rootStore.accountsStore as ServiceStore;
      if (accountsStore) {
        promises.push(accountsStore.find());
      }

      // Execute all data loading in parallel
      await Promise.allSettled(promises);

      // Load dashboard data after stores are populated
      const viewStore = (rootStore as any).estimatingDashboardViewStore;
      if (viewStore) {
        await viewStore.loadDashboardData();
      }

      return true;
    } catch (error) {
      console.error("Failed to load estimating dashboard data:", error);
      return true; // Allow navigation even if data loading fails
    }
  },
  onExit: ({ rootStore }: any) => {
    const viewStore = (rootStore as any).estimatingDashboardViewStore;
    if (viewStore) {
      viewStore.reset();
    }
  },
};

export default EstimatingDashboardRoute;
